import React, { useState, useEffect } from 'react';
import { 
  actualizarCuadranteCompleto, 
  actualizarCuadrante,
  construirIdReg, 
  decodificarIdReg,
  convertirTurnosParaAPI 
} from '../services/ApiMarcajes';
import { 
  obtenerTurnosPorCuadrante, 
  obtenerDetallesServicio 
} from '../data/FirestoreCuadrantes';
import './CuadranteUpdater.scss';

const CuadranteUpdater = ({ cuadranteId, currentService, onUpdateComplete, updateResult, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detallesServicio, setDetallesServicio] = useState(null);
  const [turnos, setTurnos] = useState([]);
  const [idRegCalculado, setIdRegCalculado] = useState('');
  const [turnosConvertidos, setTurnosConvertidos] = useState([]);
  const [localUpdateResult, setLocalUpdateResult] = useState(null);

  useEffect(() => {
    if (cuadranteId && currentService) {
      cargarDatosCuadrante();
    }
  }, [cuadranteId, currentService]);

  // Usar el resultado que viene como prop si existe
  useEffect(() => {
    if (updateResult) {
      setLocalUpdateResult(updateResult);
    }
  }, [updateResult]);

  const cargarDatosCuadrante = async () => {
    try {
      setLoading(true);
      setError('');
      setLocalUpdateResult(null);

      // Obtener turnos del cuadrante
      const turnosData = await obtenerTurnosPorCuadrante(cuadranteId, currentService);
      setTurnos(turnosData);

      if (turnosData.length === 0) {
        setError('No hay turnos en este cuadrante para actualizar');
        return;
      }

      // Obtener detalles del servicio
      const servicioId = turnosData[0].servicioId;
      if (servicioId) {
        const servicioIdToUse = Array.isArray(servicioId) ? servicioId[0] : servicioId;
        const detalles = await obtenerDetallesServicio(servicioIdToUse, currentService);
        setDetallesServicio(detalles);

        if (detalles && detalles.localizacion) {
          // Calcular ID del cuadrante
          const { contrato, delegacion, servicio } = extraerDatosLocalizacion(detalles.localizacion);
          const empresa = detalles.empresa || "0001";
          
          const primerTurno = turnosData[0];
          const fecha = new Date(primerTurno.fecha);
          const calendario = fecha.getFullYear().toString();
          const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
          
          const idReg = construirIdReg(empresa, delegacion, calendario, mes, contrato, servicio);
          setIdRegCalculado(idReg);

          // Convertir turnos al formato de la API
          const turnosAPI = await convertirTurnosParaAPI(turnosData, detalles);
          setTurnosConvertidos(turnosAPI);
        }
      }
    } catch (error) {
      console.error('Error al cargar datos del cuadrante:', error);
      setError(`Error al cargar datos: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleActualizarCuadrante = async () => {
    if (!detallesServicio) {
      setError('No se pudieron obtener los detalles del servicio');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setLocalUpdateResult(null);

      const resultado = await actualizarCuadranteCompleto(cuadranteId, currentService, detallesServicio);
      
      setLocalUpdateResult({
        tipo: 'completo',
        idReg: resultado.idReg,
        turnosEnviados: resultado.turnosEnviados,
        respuesta: resultado.resultado,
        timestamp: new Date().toLocaleString(),
        exitoso: true
      });
      
      // No mostrar mensaje de éxito
      // setSuccess(`Cuadrante actualizado correctamente. ID: ${resultado.idReg}`);
      
      if (onUpdateComplete) {
        onUpdateComplete(resultado);
      }
    } catch (error) {
      console.error('Error al actualizar cuadrante:', error);
      setError(`Error al actualizar cuadrante: ${error.message}`);
      setLocalUpdateResult({
        tipo: 'completo',
        error: error.message,
        timestamp: new Date().toLocaleString(),
        exitoso: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActualizarManual = async () => {
    if (!idRegCalculado || turnosConvertidos.length === 0) {
      setError('No hay datos suficientes para la actualización manual');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setLocalUpdateResult(null);

      const resultado = await actualizarCuadrante(idRegCalculado, turnosConvertidos);
      
      setLocalUpdateResult({
        tipo: 'manual',
        idReg: idRegCalculado,
        turnosEnviados: turnosConvertidos.length,
        respuesta: resultado,
        timestamp: new Date().toLocaleString(),
        exitoso: true
      });
      
      // No mostrar mensaje de éxito
      // setSuccess(`Cuadrante actualizado correctamente. Respuesta: ${resultado.data}`);
      
      if (onUpdateComplete) {
        onUpdateComplete({ idReg: idRegCalculado, resultado });
      }
    } catch (error) {
      console.error('Error al actualizar cuadrante manualmente:', error);
      setError(`Error al actualizar cuadrante: ${error.message}`);
      setLocalUpdateResult({
        tipo: 'manual',
        idReg: idRegCalculado,
        error: error.message,
        timestamp: new Date().toLocaleString(),
        exitoso: false
      });
    } finally {
      setLoading(false);
    }
  };

  const extraerDatosLocalizacion = (localizacion) => {
    if (!localizacion) return {};
    
    const partes = localizacion.split('/');
    if (partes.length >= 2) {
      return {
        contrato: partes[0],
        delegacion: partes[1],
        servicio: partes[2] || "01"
      };
    }
    return {};
  };

  const renderUpdateResult = () => {
    const resultToShow = localUpdateResult || updateResult;
    if (!resultToShow) return null;

    return (
      <div className={`update-result ${resultToShow.exitoso ? 'success' : 'error'}`}>
        <div className="result-header">
          <h4>Respuesta de la API</h4>
          {onClose && (
            <button 
              onClick={onClose}
              className="close-button"
              title="Cerrar modal"
            >
              ✕
            </button>
          )}
        </div>
        
        {resultToShow.respuesta && resultToShow.respuesta.data && (
          <div className="api-response">
            <div className="response-details">
              <div className="response-data">
                <strong>Contenido del campo DATA:</strong>
                <div className="data-content">
                  <pre>{resultToShow.respuesta.data}</pre>
                </div>
              </div>
            </div>
          </div>
        )}

        {resultToShow.error && (
          <div className="error-details">
            <h5>Detalles del Error</h5>
            <div className="error-message">
              <pre>{resultToShow.error}</pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="cuadrante-updater">
      <h3>Actualizar Cuadrante en API</h3>
      
      {loading && (
        <div className="loading">
          <p>Cargando datos del cuadrante...</p>
        </div>
      )}

      {error && (
        <div className="error">
          <p>Error: {error}</p>
        </div>
      )}

      {/* Removido el mensaje de éxito */}
      {/* {success && (
        <div className="success">
          <p>{success}</p>
        </div>
      )} */}

      {turnos.length > 0 && (
        <div className="cuadrante-info">
          <h4>Información del Cuadrante</h4>
          <p><strong>Total de turnos:</strong> {turnos.length}</p>
          <p><strong>ID calculado:</strong> {idRegCalculado}</p>
          {idRegCalculado && (
            <p><strong>ID decodificado:</strong> {decodificarIdReg(idRegCalculado)}</p>
          )}
          
          {turnosConvertidos.length > 0 && (
            <div className="turnos-convertidos">
              <h5>Turnos convertidos para API ({turnosConvertidos.length})</h5>
              <div className="turnos-list">
                {turnosConvertidos.map((turno, index) => (
                  <div key={index} className="turno-item">
                    <p><strong>Persona:</strong> {turno.cuadt_persona}</p>
                    <p><strong>Empresa:</strong> {turno.cuadt_emp_perso}</p>
                    <p><strong>Delegación:</strong> {turno.cuadt_deleg_perso}</p>
                    <p><strong>Días:</strong> {turno.Dias.length}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="actions">
        <button 
          onClick={handleActualizarCuadrante}
          disabled={loading || !detallesServicio}
          className="btn-primary"
        >
          {loading ? 'Actualizando...' : 'Actualizar Cuadrante Completo'}
        </button>

        <button 
          onClick={handleActualizarManual}
          disabled={loading || !idRegCalculado || turnosConvertidos.length === 0}
          className="btn-secondary"
        >
          {loading ? 'Actualizando...' : 'Actualizar Manualmente'}
        </button>

        <button 
          onClick={cargarDatosCuadrante}
          disabled={loading}
          className="btn-refresh"
        >
          Recargar Datos
        </button>
      </div>

      {renderUpdateResult()}

      {turnos.length > 0 && (
        <div className="turnos-preview">
          <h4>Vista previa de turnos ({turnos.length})</h4>
          <div className="turnos-grid">
            {turnos.slice(0, 5).map((turno, index) => (
              <div key={index} className="turno-preview">
                <p><strong>Fecha:</strong> {turno.fecha}</p>
                <p><strong>Vigilante:</strong> {turno.vigilante}</p>
                <p><strong>Horario:</strong> {turno.inicioTurno} - {turno.finTurno}</p>
              </div>
            ))}
            {turnos.length > 5 && (
              <p className="more-turnos">... y {turnos.length - 5} turnos más</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CuadranteUpdater; 